import React, { useEffect, useState } from "react";

import FilterLines from "@uui/FilterLines";
import SearchIcon from "@uui/SearchSm";

import { Divider, ToggleButton, styled, useMediaQuery } from "@mui/material";

import type { Pagination } from "@app/common/hooks/usePagination";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import Stack, { Column, Row } from "@rui/Stack";

import ReportsMenu from "./ReportsMenu";
import DateSelector from "../components/DateSelector";
import SearchBar from "../components/SearchBar/SearchBar";
import theme from "../theme";
import { ReportListView } from "../types";

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  padding: "8px 12px",
  "&.Mui-selected": {
    color: "#FFF",
    backgroundColor: theme.palette.shades.primary[500],
    "&:hover": {
      backgroundColor: theme.palette.shades.primary[500],
    },
  },
}));

interface ReportsMenuContainerProps {
  view?: ReportListView;
  pagination: Pagination;
  isLoading: boolean;
}

const ReportsMenuContainer = ({
  view = "default",
  pagination,
  isLoading,
}: ReportsMenuContainerProps) => {
  const [filter, setFilter] = useState(false);
  const [search, setSearch] = useState(false);
  const isDesktopView = useMediaQuery(theme.breakpoints.up("sm"));

  useEffect(() => {
    if (isDesktopView) {
      setFilter(true);
      setSearch(false);
    } else {
      setFilter(false);
      setSearch(false);
    }
  }, [isDesktopView]);

  const reportMenu = (
    <ReportsMenu
      hidePagination={!isDesktopView}
      pagination={pagination}
      isLoading={isLoading}
      view={view}
    />
  );

  return (
    <Stack>
      <Row
        sx={{ display: { xxs: "flex", sm: "none" } }}
        justifyContent="space-between"
        paddingTop={(theme) => theme.spaces.small}
      >
        <Row gap={(theme) => theme.spaces.small}>
          <StyledToggleButton
            value="filter"
            selected={filter}
            onChange={() => setFilter((prev) => !prev)}
          >
            <FilterLines />
            &nbsp;Filters
          </StyledToggleButton>
          <StyledToggleButton
            value="search"
            selected={search}
            onChange={() => setSearch((prev) => !prev)}
          >
            <SearchIcon sx={{ height: "16px", width: "16px" }} />
          </StyledToggleButton>
        </Row>
        <DateSelector showCustomDateRange={false} />
      </Row>
      <Divider
        sx={{
          marginY: (theme) => theme.spaces.small,
          marginX: "-24px",
          display: { xxs: "block", sm: "none" },
        }}
      />
      <Column gap={{ xxs: !filter ? 0 : "8px", sm: 0 }}>
        {isDesktopView ? (
          reportMenu
        ) : (
          <AutoAnimateContainer show={filter}>
            {reportMenu}
          </AutoAnimateContainer>
        )}
        <AutoAnimateContainer show={search}>
          <SearchBar />
        </AutoAnimateContainer>
      </Column>
    </Stack>
  );
};

export default ReportsMenuContainer;
